import { Injectable, signal } from '@angular/core';

export type ToastTone = 'success' | 'error' | 'info';

/** An offer to reverse what the toast just announced. */
export interface ToastAction {
  label: string;
  run: () => void | Promise<void>;
}

export interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
  action?: ToastAction;
}

const LIFE_MS = 4000;
/** An undo has to outlive a glance. Nothing else needs the extra time. */
const LIFE_WITH_ACTION_MS = 9000;

/** One host in the shell replaces the five <p-toast> instances and their MessageService
 *  providers. Firestore-triggered calls are already back in the zone by the time they
 *  reach here (utils/FirestoreRx). */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private nextId = 0;
  readonly toasts = signal<Toast[]>([]);

  success(message: string, action?: ToastAction): void {
    this.push('success', message, action);
  }
  error(message: string): void {
    this.push('error', message);
  }
  info(message: string): void {
    this.push('info', message);
  }

  dismiss(id: number): void {
    this.toasts.update((list) => list.filter((t) => t.id !== id));
  }

  private push(tone: ToastTone, message: string, action?: ToastAction): void {
    const id = this.nextId++;
    this.toasts.update((list) => [...list, { id, tone, message, action }]);
    setTimeout(() => this.dismiss(id), action ? LIFE_WITH_ACTION_MS : LIFE_MS);
  }
}
